<template>
	<BaseLoading
		v-if="isLoading"
	/>
	<div
		v-else
		class="main-wrapper form-custom"
	>
		<section>
			<CRow class="mb-4">
				<CCol lg="12">
					<h4 class="m-0">
						{{ $t('message.channel_rule_info') }}
					</h4>
				</CCol>
			</CRow>
		</section>

		<section>
			<CRow>
				<CCol lg="12">
					<CInput
						v-model="$v.form.name.$model"
						:label="`${$t('message.rule_name')}*`"
						:placeholder="$t('message.e_g_bangkok_area')"
						:is-valid="formState.name.valid"
						:invalid-feedback="formState.name.error"
						:disabled="isUser"
					/>
				</CCol>
			</CRow>

			<CRow>
				<CCol lg="6" class="d-flex flex-column">
					<label>
						{{ $t('message.priority') }}*
					</label>
					<span class="typo-helper-text">
						{{ $t('message.zero_is_highest_score_99_is_lowest_score') }}
					</span>
				</CCol>
				<CCol lg="6">
					<BaseInputNumber
						v-model="$v.form.priority.$model"
						:is-valid="formState.priority.valid"
						:invalid-feedback="formState.priority.error"
						:disabled="isUser"
						text-align="left"
						class="w-136px ml-auto"
					/>
				</CCol>
			</CRow>

			<CRow>
				<CCol lg="12" class="mt-4">
					<BaseSwitch
						v-model="form.status"
						:label="$t('message.status')"
						:checked-text="$t('message.active')"
						:unchecked-text="$t('message.inactive')"
					/>
				</CCol>
			</CRow>

			<CRow class="mt-4">
				<CCol>
					<hr class="mt-4 mb-4">
				</CCol>
			</CRow>
		</section>

		<section class="mt-3 mb-4">
			<CRow class="mb-4">
				<CCol lg="12">
					<h4 class="m-0">
						{{ $t('message.allocate_to_channels') }}
					</h4>
				</CCol>
			</CRow>
			<CRow class="mb-2">
				<CCol lg="4">
					<BaseDropDown
						v-model="$v.form.onlineGroup.$model"
						:options="onlineOptions"
						:label-drop-down="$t('message.for_online_group')"
						:placeholder="$t('message.select_group')"
						:searchable="false"
						:is-valid="formState.onlineGroup.valid"
						:invalid-feedback="formState.onlineGroup.error"
						:disabled="isUser"
						label="name"
						track-by="value"
					/>
				</CCol>
			</CRow>
			<SelectSkuCategory
				:type.sync="selectCategorySkuType"
				:values="selectCategorySkuValues"
				:exclude-values="selectExcludeSkuValues"
				:check-all="selectCategorySkuCheckAll"
				:is-valid="formState.categorySku.valid"
				:disabled="isUser"
				@onDone="onSelectCategorySkuDone"
				@onDoneExcludeSku="onSelectExcludeSku"
			/>
		</section>

		<section>
			<CRow>
				<CCol>
					<AllocateChannels
						v-model="form.allocateChannels"
						:total="onlineStockQty"
						:is-valid="formState.allocations.valid"
						:disabled="isUser"
					/>
				</CCol>
			</CRow>
			<CRow>
				<CCol>
					<hr class="mt-3 mb-4">
				</CCol>
			</CRow>
		</section>

		<section class="mt-2">
			<CRow>
				<CCol>
					<AllocateCondition
						:channels="channels"
						:conditions="form.allocateConditions"
						:is-valid="formState.conditions.valid"
						:disabled="isUser"
						@onDone="handleAllocateConditionDone"
					/>
				</CCol>
			</CRow>
			<CRow>
				<CCol>
					<hr class="mt-4 mb-0">
				</CCol>
			</CRow>
		</section>

		<BaseActionSubmit
			v-if="isAdmin"
			:is-loading="isSaving"
			:submit-text="$t('button.save_change')"
			:cancel-text="$t('button.cancel')"
			:remove-text="$t('button.remove')"
			@onSubmit="handleSubmit"
			@onCancel="handleCancel"
			@onRemove="handleOpenModalRemove"
		/>

		<!-- Modal Confirm Delete -->
		<ModalConfirmDelete
			ref="modal-confirm-delete"
			:show.sync="isShowModalConfirmDelete"
			:title="$t('message.do_you_want_to_remove_this')"
			:disabled-submit="isDeleting"
			@onSubmit="handleConfirmRemove"
			@onCancel="handleCancelRemove"
		/>
		<!-- End Modal Confirm Delete -->
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { required, maxLength, minValue, maxValue } from 'vuelidate/lib/validators';
import SelectSkuCategory from '../components/SelectSkuCategory.vue';
import AllocateChannels from '../components/AllocateChannels.vue';
import AllocateCondition from '../components/AllocateCondition.vue';
import { inputStateValid, inputStateError } from '../helpers/vuelidator';
import { getSelectedValue } from '../helpers/dropdown';
import { cloneDeep } from '../helpers/utils';
import { ROUTER_NAME } from '../enums/router';
import { ALLOCATION_BY } from '../enums/allocation';
import { CHANNEL_TYPE } from '../enums/channel';

export default {
	name: 'ChannelCreate',

	components: {
		SelectSkuCategory,
		AllocateChannels,
		AllocateCondition,
	},

	data() {
		return {
			form: {
				name: null,
				priority: null,
				status: false,

				onlineGroup: null,

				categoryIds: [],
				isCategoryAll: false,
				skuIds: [],
				excludeSkuIds: [],
				isSkuAll: false,
				skuCodes: [],
				excludeSkuCodes: [],

				allocateChannels: [],
				allocateConditions: [],
			},
			selectCategorySkuType: ALLOCATION_BY.CATEGOTY, // category or sku
			selectCategorySkuValues: [], // categories or sku ids
			selectExcludeSkuValues: [],
			selectCategorySkuCheckAll: false,
			onlineStockQty: 0,
			channels: [],
			isShowModalConfirmDelete: false,
			isDirty: false,
		};
	},

	computed: {
		...mapState('channelList', {
			getChannelList: 'data',
			isLoadingChannels: 'isLoading',
		}),
		...mapState('channelRuleSingle', {
			isLoadingChannelRuleSingle: 'isLoading',
			data: 'data',
			errorChannelRuleSingle: 'error',
		}),
		...mapState('channelRuleUpdate', {
			isSaving: 'isLoading',
			error: 'error',
		}),
		...mapState('channelRuleDelete', {
			isDeleting: 'isLoading',
			errorDelete: 'error',
		}),
		...mapGetters('channelRuleSingle', {
			getForm: 'getForm',
		}),
		...mapState('branchGroupList', {
			isLoadingOnlineGroups: 'isLoading',
			onlineGroups: 'data',
		}),
		...mapGetters('branchGroupList', {
			onlineOptions: 'onlineOptions',
		}),
		...mapGetters('me', {
			isUser: 'isUser',
			isAdmin: 'isAdmin',
		}),
		formState() {
			return {
				name: {
					valid: inputStateValid(this.$v.form.name),
					error: inputStateError(this.$v.form.name)(this),
				},
				priority: {
					valid: inputStateValid(this.$v.form.priority),
					error: inputStateError(this.$v.form.priority)(this),
				},
				onlineGroup: {
					valid: inputStateValid(this.$v.form.onlineGroup),
					error: inputStateError(this.$v.form.onlineGroup)(this),
				},
				categorySku: {
					valid: (() => {
						if (!this.isDirty) {
							return null;
						}

						if (this.form.categoryIds.length || this.form.isCategoryAll) {
							return true;
						}

						if (this.form.skuIds.length || this.form.isSkuAll) {
							return true;
						}

						return false;
					})(),
				},
				allocations: {
					valid: (() => {
						if (!this.isDirty) {
							return null;
						}

						const isSetAnyAllocations = this.form.allocateChannels.some(({ percentage }) => Number(percentage) > 0);

						if (isSetAnyAllocations) {
							return true;
						}

						return false;
					})(),
				},
				conditions: {
					valid: (() => {
						if (!this.isDirty) {
							return null;
						}

						if (this.form.allocateConditions.length) {
							return true;
						}

						return false;
					})(),
				},
			};
		},
		isLoading() {
			return this.isLoadingChannels || this.isLoadingChannelRuleSingle || this.isLoadingOnlineGroups;
		},
		filterOnlineChannels() {
			return this.getChannelList.filter((ch) => ch.type === CHANNEL_TYPE.ONLINE);
		},
	},

	validations: {
		form: {
			name: {
				required,
				maxLength: maxLength(255),
			},
			priority: {
				required,
				minValue: minValue(1),
				maxValue: maxValue(99),
			},
			onlineGroup: {
				required,
			},
		},
	},

	async created() {
		await this.fetchData();
		await this.getBranchGroups();

		if (this.errorChannelRuleSingle !== null) {
			this.showToast({
				header: this.$t('toast.unexpectedError'),
				content: this.errorChannelRuleSingle.message,
				type: 'danger',
			});
		}

		// set state form
		this.setState();

		// set page title
		this.setPageTitle({
			title: this.data.name,
			isShow: true,
		});

		this.filterOnlineChannels.forEach((item) => {
			this.channels.push({
				id: item.id,
				name: item.name,
			});
		});
	},

	methods: {
		...mapActions('toast', {
			showToast: 'showToast',
		}),
		...mapActions('channelList', {
			getChannels: 'getChannels',
		}),
		...mapActions('channelRuleSingle', {
			getChannelRule: 'getChannelRule',
		}),
		...mapActions('channelRuleUpdate', {
			updateChannelRule: 'updateChannelRule',
		}),
		...mapActions('channelRuleDelete', {
			removeChannelRule: 'removeChannelRule',
		}),
		...mapActions('pageTitle', {
			setPageTitle: 'setPageTitle',
		}),
		fetchData() {
			return Promise.all([
				this.getChannels(),
				this.getChannelRule(this.$route.params.id),
			]);
		},
		...mapActions('branchGroupList', {
			getBranchGroups: 'getBranchGroups',
		}),
		setState() {
			// set form
			this.form = cloneDeep(this.getForm(this.filterOnlineChannels));

			const isTypeSku = this.form.isSkuAll || this.form.skuIds.length;

			// set sku/category type
			if (isTypeSku) {
				this.selectCategorySkuType = ALLOCATION_BY.SKU;
			} else {
				this.selectCategorySkuType = ALLOCATION_BY.CATEGOTY;
			}

			// set sku/category values
			if (isTypeSku) {
				this.selectCategorySkuValues = this.form.skuCodes;
			} else {
				this.selectCategorySkuValues = this.form.categoryIds;
			}

			this.selectExcludeSkuValues = this.form.excludeSkuCodes;

			// set dropdown value
			this.form.onlineGroup = getSelectedValue(this.data.branchGroupId, this.onlineOptions);

			// set is check all
			this.selectedBranchCheckAll = this.form.isBranchAll;
			this.selectCategorySkuCheckAll = this.form.isSkuAll || this.form.isCategoryAll;

			// set online group
			this.form.onlineGroup = getSelectedValue(this.data.branchGroupId, this.onlineOptions);
		},
		async handleSubmit() {
			this.$v.$touch();

			this.isDirty = true;

			const isValidForm = !this.$v.$invalid && this.formState.categorySku.valid && this.formState.allocations.valid && this.formState.conditions.valid;

			if (isValidForm) {
				const getCategorySkuKeys = (form = {}) => {
					const keys = {};
					const isTabCategory = this.selectCategorySkuType === ALLOCATION_BY.CATEGOTY;
					const isTabSku = this.selectCategorySkuType === ALLOCATION_BY.SKU;
					if (isTabCategory) {
						keys.is_all_categories = form.isCategoryAll;
						keys.category_ids = form.isCategoryAll ? [] : form.categoryIds;
					} else if (isTabSku) {
						keys.is_all_products = false;
						keys.itec_skus = form.isSkuAll ? [] : form.skuIds;
						keys.product_ids = [];
					}
					return keys;
				};

				const getChannelAllocations = (form) => {
					return {
						channel_allocations: (() => {
							return form.allocateChannels.map((item) => {
								return {
									channel_id: item.id,
									percentage: Number(item.percentage || 0),
								};
							});
						})(),
					};
				};

				const getConditions = (form) => {
					return {
						conditions: (() => {
							return form.allocateConditions.map((item) => {
								return {
									condition_qty: Number(item.minQty || 0),
									allocations: item.channels.map((_item) => {
										return {
											channel_id: _item.id,
											qty: Number(_item.qty || 0),
										};
									}),
								};
							});
						})(),
					};
				};

				const data = {
					name: this.form.name,
					priority: Number(this.form.priority || 1),
					branch_group_id: this.form.onlineGroup.value,
					...getCategorySkuKeys(this.form),
					...getChannelAllocations(this.form),
					...getConditions(this.form),
					itec_sku_exclusions: this.form.excludeSkuIds,
					is_active: Boolean(this.form.status),
				};

				await this.updateChannelRule({
					id: this.$route.params.id,
					params: data,
				});

				const { error } = this;

				if (error === null) {
					this.showToast({
						header: this.$t('toast.success'),
						content: this.$t('toast.update_rule'),
						type: 'success',
					});
					await this.fetchData();
					this.setState();
				} else {
					this.showToast({
						header: this.$t('toast.unable_to_save'),
						content: error.message,
						type: 'danger',
						timeout: 10000, // 10 secs
					});
				}
			}

			return isValidForm;
		},
		handleCancel() {
			// redirect to list pages
			this.$router.push({ name: ROUTER_NAME.ALOCATION_CHANNEL_LIST });
		},
		handleOpenModalRemove() {
			this.$refs['modal-confirm-delete'].open();
		},
		async handleConfirmRemove() {
			await this.removeChannelRule(this.$route.params.id);

			if (this.errorDelete === null) {
				this.isShowModalConfirmDelete = false;

				this.showToast({
					header: this.$t('toast.success'),
					content: this.$t('toast.data_has_been_removed'),
					type: 'success',
				});

				// redirect to list pages
				this.$router.push({ name: ROUTER_NAME.ALOCATION_CHANNEL_LIST });
			} else {
				this.showToast({
					header: this.$t('toast.unable_to_remove'),
					content: this.errorDelete.message,
					type: 'danger',
				});
			}
		},
		handleCancelRemove() {
			this.isShowModalConfirmDelete = false;
		},
		handleAllocateConditionDone(conditions = []) {
			this.form.allocateConditions = conditions;
		},
		onSelectCategorySkuDone(type = '', values = [], isSelectedAll = false) {
			this.selectCategorySkuType = type;

			if (type === ALLOCATION_BY.CATEGOTY) {
				this.form.categoryIds = values.map(({ id }) => id);
				this.form.isCategoryAll = isSelectedAll;
			} else if (type === ALLOCATION_BY.SKU) {
				this.form.skuIds = values;
				this.form.isSkuAll = isSelectedAll;
			}
		},
		onSelectExcludeSku(values = []) {
			this.form.excludeSkuIds = values;
		},
	},
};
</script>

<style lang="scss" scoped>
.form-custom {
	width: rem(600);
}
</style>
